import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicShell from "@/components/PublicShell";
import MoneyInMotion from "@/components/MoneyInMotion";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  ShieldCheck,
  ArrowDownToLine,
  ArrowUpFromLine,
  Send,
  CreditCard,
  Sparkles,
  Building2,
  User,
  Beaker,
  Banknote,
  ScanFace,
  KeyRound,
  Fingerprint,
  CheckCircle2,
  Globe,
  Lock,
  Clock,
  ChevronDown,
} from "lucide-react";

const AUTH_BG = "https://static.prod-images.emergentagent.com/jobs/06b5cb7e-c888-4c81-b88c-5244c1d0af32/images/fd1c566f5b8864f6832e4d35cf9140f3939f77c249aa2aa7f7d74ab738a59602.png";
const ZEN = "https://static.prod-images.emergentagent.com/jobs/06b5cb7e-c888-4c81-b88c-5244c1d0af32/images/cc59150a93d9427a62637e0fbdd0dfcfece6cc59cabc4e8149e67d6bd7803663.png";

const FEATURES = [
  {
    Icon: ArrowDownToLine,
    title: "Deposit from any card",
    body: "Top up your wallet from any external bank card via PayPal. Funds land instantly — no transfers, no waiting.",
  },
  {
    Icon: Send,
    title: "Send to anyone, instantly",
    body: "Transfer to other FinVault users using their email or wallet ID. Settlement is real-time, with notes attached for context.",
  },
  {
    Icon: ArrowUpFromLine,
    title: "Withdraw to any bank",
    body: "Move money out to any external bank account in seconds. Daily settlement at no extra fee.",
  },
  {
    Icon: CreditCard,
    title: "Card details, PCI-aware",
    body: "Manage saved funding methods. We store only the brand, holder, and last four digits — never the full PAN or CVV.",
  },
];

const SECURITY = [
  { Icon: KeyRound, label: "Email OTP at every login" },
  { Icon: Fingerprint, label: "Optional authenticator-app 2FA" },
  { Icon: ShieldCheck, label: "Bcrypt-hashed passwords" },
  { Icon: Lock, label: "HttpOnly · Secure · SameSite cookies" },
  { Icon: ScanFace, label: "Liveness check during KYC" },
  { Icon: Globe, label: "Brute-force lockouts on every endpoint" },
];

const FAQ = [
  {
    q: "Do I need a bank to use FinVault?",
    a: "No. Open a Demo account in under a minute and explore the entire app with $1,000 in simulated funds. When you're ready, switch to Live and connect a card to deposit real money.",
  },
  {
    q: "How are deposits processed?",
    a: "Live deposits go through PayPal Checkout, which accepts any external bank card. FinVault never sees or stores your card number — PayPal handles that end-to-end.",
  },
  {
    q: "What happens to my KYC documents?",
    a: "They are encrypted in transit, retained only for compliance review, and reviewable only by FinVault's compliance team. You can request deletion at any time.",
  },
  {
    q: "Can a business open an account?",
    a: "Yes. Choose 'Business' during registration and supply your registered business name plus the standard KYC documents. New business accounts get 90 days with zero monthly fee.",
  },
  {
    q: "How is my password protected?",
    a: "Passwords are hashed with bcrypt before storage. Sessions use signed JWTs delivered via httpOnly cookies, and every login is verified with a one-time email code or your authenticator app.",
  },
];

export default function Landing() {
  return (
    <PublicShell>
      {/* HERO */}
      <section className="max-w-[1280px] mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-16 grid lg:grid-cols-12 gap-10 items-center" data-testid="landing-hero">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#D2CDBE] text-xs text-[#4F4D45] mb-6" data-testid="landing-hero-eyebrow">
            <Sparkles className="w-3.5 h-3.5 text-[#A85943]" />
            <span>2026 · Built on the new banking stack</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="landing-hero-title">
            A wallet that feels<br />
            <span className="text-[#1B3526]">grounded.</span>{" "}
            <span className="text-[#A85943]">Secure.</span>{" "}
            <span className="italic font-medium">Calm.</span>
          </h1>
          <p className="mt-6 text-lg text-[#4F4D45] leading-relaxed max-w-xl" data-testid="landing-hero-body">
            Deposit from your bank card, transfer instantly, and withdraw to any account — across Personal & Business profiles, in Demo or Live mode. Protected by email verification, biometric KYC, and TOTP 2FA.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/register" data-testid="landing-hero-register">
              <Button className="bg-[#1B3526] hover:bg-[#0F2517] text-white rounded-lg px-6 py-6 text-base">
                Open your account <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/login" data-testid="landing-hero-login">
              <Button variant="outline" className="border-[#1B3526] text-[#1B3526] hover:bg-[#1B3526] hover:text-white rounded-lg px-6 py-6 text-base">
                I already have one
              </Button>
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-xl text-sm text-[#4F4D45]" data-testid="landing-hero-trust">
            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#1B3526]" /> OTP on every login</div>
            <div className="flex items-center gap-2"><Fingerprint className="w-4 h-4 text-[#1B3526]" /> Biometric KYC</div>
            <div className="flex items-center gap-2"><Lock className="w-4 h-4 text-[#1B3526]" /> PCI-aware cards</div>
          </div>
        </div>
        <div className="lg:col-span-5 relative">
          <div className="relative rounded-3xl overflow-hidden border border-[#D2CDBE] aspect-[4/5] bg-[#E3DECF]" data-testid="landing-hero-media">
            <img src={AUTH_BG} alt="FinVault hero" className="absolute inset-0 w-full h-full object-cover" />
            <video
              src="/hero.mp4"
              poster={AUTH_BG}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              data-testid="landing-hero-video"
            />
            <div className="absolute bottom-5 left-5 right-5 bg-white/85 backdrop-blur-md border border-[#D2CDBE] rounded-2xl p-5">
              <div className="text-eyebrow text-[#4F4D45] mb-2">Live balance</div>
              <div className="font-display text-3xl font-bold tracking-tight">$ 12,480.00</div>
              <div className="text-xs text-[#4F4D45] mt-1">+ $320 this week · stress-free spending</div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS BAND */}
      <section className="border-y border-[#D2CDBE] bg-white/60">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center" data-testid="landing-stats">
          <Stat value="< 60s" label="To open an account" />
          <Stat value="0%" label="Hidden fees" />
          <Stat value="24 / 7" label="Wallet uptime" />
          <Stat value="2FA" label="Optional for every account" />
        </div>
      </section>

      {/* LIVE TICKER · MONEY IN MOTION */}
      <MoneyInMotion />

      {/* PRODUCT WALKTHROUGH */}
      <section className="max-w-[1280px] mx-auto px-6 md:px-10 py-20" data-testid="landing-walkthrough">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="text-eyebrow text-[#4F4D45]">90 seconds, end to end</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight mt-2">From signup to first transfer.</h2>
            <p className="text-[#4F4D45] mt-4 leading-relaxed">A 30-second walkthrough of what your first day on FinVault feels like — calm, secure, and faster than you'd think.</p>
            <ol className="mt-8 space-y-5" data-testid="landing-walkthrough-steps">
              {[
                { n: "01", t: "Verify in under a minute", d: "Email + selfie + ID. Our AI auto-approves crisp matches; humans review the rest." },
                { n: "02", t: "Add funds securely", d: "Deposit straight from your bank card through PayPal — we never touch your PAN." },
                { n: "03", t: "Send by QR code", d: "Scan a friend's account QR from another phone. Recipient confirmed, money sent." },
              ].map((s) => (
                <li key={s.n} className="flex gap-4">
                  <div className="font-mono text-xs text-[#4F4D45] mt-1">{s.n}</div>
                  <div>
                    <div className="font-display text-lg font-medium">{s.t}</div>
                    <div className="text-sm text-[#4F4D45] mt-1 leading-relaxed">{s.d}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <div className="lg:col-span-7">
            <div className="relative rounded-3xl overflow-hidden border border-[#D2CDBE] aspect-video bg-[#0E1A14]">
              <video
                src="/walkthrough.mp4"
                poster={AUTH_BG}
                controls
                muted
                playsInline
                preload="metadata"
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                data-testid="landing-walkthrough-video"
              />
              <div className="absolute inset-0 -z-10 bg-[#15211A] flex items-center justify-center text-[#9DA39A] text-sm">
                Walkthrough video coming soon
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-[1280px] mx-auto px-6 md:px-10 py-20" data-testid="landing-features">
        <div className="max-w-2xl">
          <div className="text-eyebrow text-[#4F4D45]">What's inside</div>
          <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight mt-2">Every move, in one place.</h2>
          <p className="text-[#4F4D45] mt-4 leading-relaxed">Four core actions, designed to feel quiet and confident: deposit, transfer, withdraw, and manage cards. Nothing extra. Nothing in the way.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((f, i) => (
            <div key={i} className="fv-card p-6 group hover:border-[#1B3526] transition-colors" data-testid={`landing-feature-${i}`}>
              <div className="w-11 h-11 rounded-lg bg-[#1B3526]/10 text-[#1B3526] flex items-center justify-center mb-5 group-hover:bg-[#1B3526] group-hover:text-white transition-colors">
                <f.Icon className="w-5 h-5" strokeWidth={1.6} />
              </div>
              <h3 className="font-display text-xl font-medium leading-snug">{f.title}</h3>
              <p className="text-sm text-[#4F4D45] mt-2 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ACCOUNT TYPES */}
      <section className="bg-[#E3DECF]/60 border-y border-[#D2CDBE]" data-testid="landing-account-types">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-20 grid lg:grid-cols-2 gap-6">
          <AccountTile
            Icon={User}
            tag="Personal"
            title="Quiet, premium banking for the everyday."
            body="Deposits, transfers, withdrawals and saved cards — calmly designed for the way you actually move money."
            bullets={["$1,000 demo on signup", "Email & TOTP 2FA included", "Free transfers between FinVault users"]}
            cta={{ label: "Open a Personal account", to: "/register" }}
            testId="landing-account-personal"
          />
          <AccountTile
            Icon={Building2}
            tag="Business"
            title="Scale revenue, payouts and team finance."
            body="A grown-up account for companies. Card-based deposits, instant transfers, KYC-verified, and audit-ready."
            bullets={["90 days · zero monthly fee", "Business KYC built in", "Receive payments via wallet ID"]}
            cta={{ label: "Open a Business account", to: "/register?promo=BIZ90" }}
            testId="landing-account-business"
          />
        </div>
      </section>

      {/* DEMO VS LIVE */}
      <section className="max-w-[1280px] mx-auto px-6 md:px-10 py-20" data-testid="landing-modes">
        <div className="max-w-2xl">
          <div className="text-eyebrow text-[#4F4D45]">Two modes, one app</div>
          <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight mt-2">Try it, then use it.</h2>
          <p className="text-[#4F4D45] mt-4 leading-relaxed">Every FinVault account ships with two wallets side by side — a Demo wallet for learning, and a Live wallet for actual money. Switch at any time. Nothing crosses over.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 gap-5">
          <ModeTile
            Icon={Beaker}
            tag="Demo"
            title="A safe sandbox"
            body="Start with $1,000 of simulated funds, run the entire wallet flow, transfer, withdraw — and learn how FinVault behaves before you connect any real card."
            tint="bg-[#B58957]/15"
            iconTint="text-[#8A4632]"
            testId="landing-mode-demo"
          />
          <ModeTile
            Icon={Banknote}
            tag="Live"
            title="Real money, gentle controls"
            body="Connect a bank card via PayPal, deposit funds, and start moving money. Every action is protected by OTP, optional 2FA, and our brute-force lockouts."
            tint="bg-[#1B3526]/10"
            iconTint="text-[#1B3526]"
            testId="landing-mode-live"
          />
        </div>
      </section>

      {/* SECURITY */}
      <section className="bg-[#0A0A08] text-white" data-testid="landing-security">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="text-eyebrow opacity-70">Security, on by default</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight mt-2 leading-tight">Built with the safe-by-default mindset of 2026.</h2>
            <p className="opacity-80 mt-4 leading-relaxed">No hidden CVV storage. No password kept in plaintext. No login without a second factor. We don't sprinkle security on top — it's the way the wallet is wired together.</p>
            <Link to="/about" className="inline-flex items-center gap-2 mt-8 text-sm text-[#B58957] hover:text-white transition-colors" data-testid="landing-security-cta">
              Read the security overview <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
            {SECURITY.map((s, i) => (
              <div key={i} className="flex items-center gap-3 px-5 py-4 rounded-xl bg-white/[0.06] border border-white/10" data-testid={`landing-security-item-${i}`}>
                <div className="w-9 h-9 rounded-lg bg-[#B58957]/20 text-[#B58957] flex items-center justify-center">
                  <s.Icon className="w-4 h-4" strokeWidth={1.6} />
                </div>
                <span className="text-sm">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* KYC / LIVENESS */}
      <section className="max-w-[1280px] mx-auto px-6 md:px-10 py-20 grid lg:grid-cols-12 gap-10 items-center" data-testid="landing-kyc">
        <div className="lg:col-span-5 order-2 lg:order-1">
          <div className="relative rounded-3xl overflow-hidden border border-[#D2CDBE] aspect-square bg-[#E3DECF]">
            <img src={ZEN} alt="Liveness check" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute top-4 left-4 right-4 flex justify-between text-xs">
              <span className="bg-white/85 backdrop-blur-md px-3 py-1.5 rounded-full text-[#0A0A08] font-medium">3 / 3 frames</span>
              <span className="bg-[#1B3526] text-white px-3 py-1.5 rounded-full inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> liveness passed</span>
            </div>
          </div>
        </div>
        <div className="lg:col-span-7 order-1 lg:order-2">
          <div className="text-eyebrow text-[#4F4D45]">KYC, the modern way</div>
          <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight mt-2 leading-tight">A 60-second onboarding, with liveness baked in.</h2>
          <p className="text-[#4F4D45] mt-4 leading-relaxed max-w-xl">Submit your photograph, government ID, and proof of address. Then a friendly liveness flow — look straight, blink, smile — captures three frames that compliance reviews privately. No back-and-forth emails. No clunky portals.</p>
          <ul className="mt-8 space-y-3 text-sm">
            {[
              "Photo · Government ID · Proof of address",
              "3-frame liveness capture during signup",
              "Compliance review with approve / reject decisions",
              "Full data export and deletion on request",
            ].map((t, i) => (
              <li key={i} className="flex items-center gap-3 text-[#0A0A08]" data-testid={`landing-kyc-step-${i}`}>
                <CheckCircle2 className="w-4 h-4 text-[#1B3526]" /> {t}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* TIMELINE / HOW IT WORKS */}
      <section className="bg-[#E3DECF]/60 border-y border-[#D2CDBE]" data-testid="landing-timeline">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-20">
          <div className="max-w-2xl">
            <div className="text-eyebrow text-[#4F4D45]">How it works</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight mt-2">Four steps. No detours.</h2>
          </div>
          <ol className="mt-12 grid md:grid-cols-4 gap-5">
            {[
              { t: "Register", b: "Choose Personal or Business, Demo or Live. Verify your email with a one-time code." },
              { t: "Verify yourself", b: "Upload three documents and complete the liveness check. Compliance reviews quickly." },
              { t: "Fund the wallet", b: "Demo? Start with $1,000 free. Live? Deposit any amount with your bank card via PayPal." },
              { t: "Move money", b: "Transfer to other FinVault users instantly. Withdraw to any external bank when you need to." },
            ].map((step, i) => (
              <li key={i} className="fv-card p-6 relative overflow-hidden" data-testid={`landing-timeline-step-${i}`}>
                <div className="absolute -top-6 -right-4 font-display text-7xl font-bold text-[#1B3526]/[0.06] select-none">0{i + 1}</div>
                <div className="text-eyebrow text-[#4F4D45] relative">Step {i + 1}</div>
                <div className="font-display text-xl font-medium mt-2 relative">{step.t}</div>
                <p className="text-sm text-[#4F4D45] mt-2 leading-relaxed relative">{step.b}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-[1280px] mx-auto px-6 md:px-10 py-20" data-testid="landing-faq">
        <div className="max-w-2xl">
          <div className="text-eyebrow text-[#4F4D45]">Questions, answered</div>
          <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight mt-2">Things people ask before signing up.</h2>
        </div>
        <div className="mt-10 grid lg:grid-cols-2 gap-3">
          {FAQ.map((item, i) => <FaqRow key={i} item={item} idx={i} />)}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-[1280px] mx-auto px-6 md:px-10 pb-20">
        <div className="relative fv-card p-10 md:p-16 bg-[#1B3526] text-white overflow-hidden" data-testid="landing-cta">
          <div className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-[#264C39]/40 blur-3xl" />
          <div className="absolute right-10 top-10 text-[#B58957] hidden md:block">
            <Clock className="w-12 h-12 opacity-60" strokeWidth={1.2} />
          </div>
          <div className="relative max-w-2xl">
            <div className="text-eyebrow opacity-80">It takes under a minute</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-2">Ready to feel calmer about money?</h2>
            <p className="opacity-80 mt-4 leading-relaxed">Open a Demo account first if you'd like. Switch to Live whenever you're ready. We'll be here either way.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="landing-cta-register">
                <Button className="bg-white text-[#0A0A08] hover:bg-white/90 rounded-lg px-6 py-6 text-base">
                  Open an account <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/login" data-testid="landing-cta-login">
                <Button variant="outline" className="border-white/40 text-white hover:bg-white/10 hover:text-white rounded-lg px-6 py-6 text-base bg-transparent">
                  Sign in
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-display text-3xl md:text-4xl font-bold text-[#0A0A08]">{value}</div>
      <div className="text-xs uppercase tracking-[0.18em] text-[#4F4D45] mt-2">{label}</div>
    </div>
  );
}

function AccountTile({ Icon, tag, title, body, bullets, cta, testId }) {
  return (
    <div className="fv-card p-8 md:p-10 flex flex-col" data-testid={testId}>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-lg bg-[#1B3526] text-white flex items-center justify-center">
          <Icon className="w-5 h-5" strokeWidth={1.6} />
        </div>
        <span className="text-eyebrow text-[#4F4D45]">{tag}</span>
      </div>
      <h3 className="font-display text-2xl md:text-3xl font-medium leading-tight">{title}</h3>
      <p className="text-[#4F4D45] mt-3 leading-relaxed">{body}</p>
      <ul className="mt-6 space-y-2 text-sm">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-center gap-2 text-[#0A0A08]">
            <CheckCircle2 className="w-4 h-4 text-[#1B3526]" /> {b}
          </li>
        ))}
      </ul>
      <Link to={cta.to} className="mt-8 self-start" data-testid={`${testId}-cta`}>
        <Button className="bg-[#1B3526] hover:bg-[#0F2517] rounded-lg">
          {cta.label} <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </Link>
    </div>
  );
}

function ModeTile({ Icon, tag, title, body, tint, iconTint, testId }) {
  return (
    <div className="fv-card p-8 flex items-start gap-5" data-testid={testId}>
      <div className={`w-12 h-12 rounded-xl ${tint} ${iconTint} flex items-center justify-center shrink-0`}>
        <Icon className="w-6 h-6" strokeWidth={1.6} />
      </div>
      <div>
        <div className="text-eyebrow text-[#4F4D45]">{tag}</div>
        <h3 className="font-display text-xl font-medium mt-1">{title}</h3>
        <p className="text-sm text-[#4F4D45] mt-2 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

function FaqRow({ item, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className={`fv-card overflow-hidden transition-colors ${open ? "border-[#1B3526]" : ""}`} data-testid={`landing-faq-${idx}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left px-6 py-5 flex items-center justify-between gap-4"
        data-testid={`landing-faq-toggle-${idx}`}
      >
        <span className="font-display text-base md:text-lg font-medium">{item.q}</span>
        <ChevronDown className={`w-4 h-4 text-[#4F4D45] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-6 pb-5 text-sm text-[#4F4D45] leading-relaxed" data-testid={`landing-faq-answer-${idx}`}>
          {item.a}
        </div>
      )}
    </div>
  );
}
